import { z } from 'zod';
import { type Language, type LocalizedText, zLocalizedText } from '@/shared/api';
import { emptyLocalizedText, localizedTextInput, localizedTextOf } from './localizedText';

/**
 * Строка «название — значение» или шаг «заголовок — пояснение» (ТЗ EXP BR-EXP-05/34/37: детали,
 * как проходит, как добраться, проживание, программа, что входит, как забронировать): две части
 * LocalizedText. Части контракта — `label`/`value` у пар и `title`/`text` у шагов; в форме — `a`/`b`.
 */
export interface PairRow {
  key: string;
  a: LocalizedText;
  b: LocalizedText;
}

/** Имена частей строки в контракте: `['label', 'value']` или `['title', 'text']`. */
export type PairParts = readonly [string, string];

export const PAIR_PARTS = ['label', 'value'] as const;
export const STEP_PARTS = ['title', 'text'] as const;

let rowCounter = 0;

/** Ключ строки для React (порядок строк меняется). */
export function pairRowKey(): string {
  rowCounter += 1;
  return `pair-${rowCounter}`;
}

export function emptyPairRow(languages: readonly Language[]): PairRow {
  return { key: pairRowKey(), a: emptyLocalizedText(languages), b: emptyLocalizedText(languages) };
}

/**
 * Строка пар без языка (`PairsField` с `plain`): значения настроек интеграции — «ключ — значение»
 * обычными строками (`key_value` формы интеграции).
 */
export interface PlainPairRow {
  key: string;
  a: string;
  b: string;
}

export function plainPairRow(a = '', b = ''): PlainPairRow {
  return { key: pairRowKey(), a, b };
}

function localizedPart(item: object, part: string): LocalizedText {
  const value = Object.entries(item).find(([key]) => key === part)?.[1];
  const parsed = zLocalizedText.safeParse(value);
  return parsed.success ? parsed.data : {};
}

/** Строки формы из ответа сервера (`PairOut` / `StepOut`). */
export function pairRowsOf(items: readonly object[], parts: PairParts): PairRow[] {
  return items.map((item) => ({
    key: pairRowKey(),
    a: localizedPart(item, parts[0]),
    b: localizedPart(item, parts[1]),
  }));
}

/** Строки формы из текстов по умолчанию (`[название, значение]` на языке ru рынка). */
export function pairRowsFromTexts(
  texts: readonly (readonly [string, string])[],
  languages: readonly Language[],
): PairRow[] {
  const language = languages[0] ?? 'ru';
  return texts.map(([a, b]) => ({
    key: pairRowKey(),
    a: { ...emptyLocalizedText(languages), [language]: a },
    b: { ...emptyLocalizedText(languages), [language]: b },
  }));
}

function isEmpty(value: LocalizedText, languages: readonly Language[]): boolean {
  return languages.every((language) => localizedTextOf(value, language).trim() === '');
}

/**
 * Строки для запроса: строки без текста не отправляются; `dropEmptyValue` — строка без значения тоже
 * (у «Как добраться» пустые «Дорога» и «Трансфер» берутся из профиля винодельни, BR-EXP-34).
 * Тексты — языков рынка без пустых (`localizedTextInput`); раздел раскладывает `a`/`b` по полям
 * контракта (`label`/`value`, `title`/`text`).
 */
export function pairsInput(
  rows: readonly PairRow[],
  languages: readonly Language[],
  options: { dropEmptyValue?: boolean } = {},
): { a: LocalizedText; b: LocalizedText }[] {
  return rows
    .filter((row) => !isEmpty(row.a, languages))
    .filter((row) => !options.dropEmptyValue || !isEmpty(row.b, languages))
    .map((row) => ({
      a: localizedTextInput(row.a, languages) ?? {},
      b: localizedTextInput(row.b, languages) ?? {},
    }));
}

export interface PairRowsRules {
  languages: readonly Language[];
  maxA: number;
  maxB: number;
  /** Наибольшее число строк (у сервера — 20). */
  maxRows?: number;
  /** Строка без значения допустима (её не отправляют — «Как добраться»). */
  optionalValue?: boolean;
  messages: { required: string; tooLong: (max: number) => string; tooMany: string };
}

/**
 * Проверка строк: у заполненной строки обязательны обе части (кроме `optionalValue`), длины — по
 * языкам (путь `поле.N.a.en`), полностью пустая строка не проверяется (её не отправляют).
 */
export function pairRowsSchema({
  languages,
  maxA,
  maxB,
  maxRows = 20,
  optionalValue = false,
  messages,
}: PairRowsRules) {
  const row = z.object({ key: z.string(), a: zLocalizedText, b: zLocalizedText });
  return z.array(row).superRefine((rows, context) => {
    const filled = rows.filter(
      (item) => !isEmpty(item.a, languages) || !isEmpty(item.b, languages),
    );
    if (filled.length > maxRows) {
      context.addIssue({ code: 'custom', message: messages.tooMany });
    }
    rows.forEach((item, index) => {
      const emptyA = isEmpty(item.a, languages);
      const emptyB = isEmpty(item.b, languages);
      if (emptyA && emptyB) return;
      if (emptyA) {
        context.addIssue({ code: 'custom', path: [index, 'a'], message: messages.required });
      }
      if (emptyB && !optionalValue) {
        context.addIssue({ code: 'custom', path: [index, 'b'], message: messages.required });
      }
      for (const language of languages) {
        if (localizedTextOf(item.a, language).length > maxA) {
          context.addIssue({
            code: 'custom',
            path: [index, 'a', language],
            message: messages.tooLong(maxA),
          });
        }
        if (localizedTextOf(item.b, language).length > maxB) {
          context.addIssue({
            code: 'custom',
            path: [index, 'b', language],
            message: messages.tooLong(maxB),
          });
        }
      }
    });
  });
}
